// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Plus, Loader2, Trash2, Edit, HelpCircle, Upload,
  ChevronDown, ChevronRight, ChevronLeft, Sparkles, Database,
  BookOpen, Wand2
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DIFFICULTY_LABELS = { junior: "Junior", confirmed: "Confirmé", senior: "Senior", expert: "Expert" };
const DIFFICULTY_COLORS = {
  junior: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30",
  senior: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30",
  expert: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30"
};
const TYPE_LABELS = { open: "Ouverte", qcm: "QCM", situation: "Mise en situation", practical: "Pratique", followup: "Relance" };

// Category hierarchy config
const CATEGORY_CONFIG = {
  "Entretien technique": { emoji: "🔧", color: "text-blue-500" },
  "Entretien RH / Soft skills": { emoji: "🤝", color: "text-green-500" },
  "Entretien commercial / Recrutement": { emoji: "💼", color: "text-purple-500" },
  "Entretien client / Prestation": { emoji: "📋", color: "text-amber-500" },
};

export default function InterviewQuestionsPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("__all__");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ domain: "", subdomain: "", difficulty: "mixed", count: 5, customDomain: "", customSubdomain: "" });
  const [form, setForm] = useState({
    question_text: "", type: "open", difficulty: "confirmed", domain: "", subdomain: "",
    tags: "", expected_answer: "", key_points: "", max_score: 10, weight: 1, estimated_minutes: 5, is_active: true,
  });

  useEffect(() => { loadData(); }, [workspaceId]);

  const loadData = async () => {
    try {
      const res = await interviewAPI.listQuestions(workspaceId, { limit: 500 });
      setQuestions(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally { setLoading(false); }
  };

  // Build category tree from questions
  const categoryTree = useMemo(() => {
    const tree = {};
    questions.forEach(q => {
      const domain = q.domain || "Non classé";
      const subdomain = q.subdomain || "";
      if (!tree[domain]) tree[domain] = { questions: [], subdomains: {} };
      if (subdomain) {
        if (!tree[domain].subdomains[subdomain]) tree[domain].subdomains[subdomain] = [];
        tree[domain].subdomains[subdomain].push(q);
      } else {
        tree[domain].questions.push(q);
      }
    });
    return tree;
  }, [questions]);

  // Filter questions for display
  const filteredQuestions = useMemo(() => {
    let filtered = [...questions];

    if (selectedCategory) {
      filtered = filtered.filter(q => q.domain === selectedCategory);
    }
    if (selectedSubcategory) {
      filtered = filtered.filter(q => q.subdomain === selectedSubcategory);
    }
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(q =>
        q.question_text.toLowerCase().includes(s) ||
        (q.expected_answer || "").toLowerCase().includes(s) ||
        (q.domain || "").toLowerCase().includes(s) ||
        (q.subdomain || "").toLowerCase().includes(s)
      );
    }
    if (filterDifficulty !== "__all__") {
      filtered = filtered.filter(q => q.difficulty === filterDifficulty);
    }

    // Group by subdomain for display
    const grouped = {};
    filtered.forEach(q => {
      const key = q.subdomain || q.domain || "Non classé";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(q);
    });

    return { filtered, grouped };
  }, [questions, selectedCategory, selectedSubcategory, search, filterDifficulty]);

  const toggleAnswer = (id) => setExpandedAnswers(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleCategory = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const selectCategory = (cat) => {
    setSelectedCategory(selectedCategory === cat ? null : cat);
    setSelectedSubcategory(null);
  };

  const selectSubcategory = (sub) => {
    setSelectedSubcategory(selectedSubcategory === sub ? null : sub);
  };

  const openCreate = () => {
    setEditQuestion(null);
    setForm({ question_text: "", type: "open", difficulty: "confirmed", domain: selectedCategory || "", subdomain: selectedSubcategory || "", tags: "", expected_answer: "", key_points: "", max_score: 10, weight: 1, estimated_minutes: 5, is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditQuestion(q);
    setForm({
      question_text: q.question_text, type: q.type, difficulty: q.difficulty,
      domain: q.domain, subdomain: q.subdomain, tags: (q.tags || []).join(", "),
      expected_answer: q.expected_answer, key_points: (q.key_points || []).join("\n"),
      max_score: q.max_score, weight: q.weight, estimated_minutes: q.estimated_minutes, is_active: q.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error("La question est requise"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        key_points: form.key_points ? form.key_points.split("\n").map(k => k.trim()).filter(Boolean) : [],
      };
      if (editQuestion) {
        await interviewAPI.updateQuestion(editQuestion.id, payload);
        toast.success("Question mise à jour");
      } else {
        await interviewAPI.createQuestion(workspaceId, user?.id, payload);
        toast.success("Question créée");
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await interviewAPI.deleteQuestion(deleteId);
      toast.success("Question supprimée");
      setDeleteId(null);
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await interviewAPI.seedQuestions(workspaceId, user?.id);
      if (res.data.success) {
        toast.success(`${res.data.seeded_count} questions ajoutées !`);
        loadData();
      } else {
        toast.info(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du seed");
    } finally { setSeeding(false); }
  };

  const handleAIGenerate = async (resolvedDomain, resolvedSubdomain) => {
    const domain = resolvedDomain || aiForm.domain;
    const subdomain = resolvedSubdomain !== undefined ? resolvedSubdomain : aiForm.subdomain;
    if (!domain) { toast.error("Sélectionnez un domaine"); return; }
    setAiGenerating(true);
    try {
      const res = await interviewAPI.generateAIQuestions(workspaceId, user?.id, {
        domain,
        subdomain,
        difficulty: aiForm.difficulty,
        count: aiForm.count,
      });
      toast.success(`${res.data.generated_count} questions générées par IA !`);
      setAiDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération IA");
    } finally { setAiGenerating(false); }
  };

  const handleGenerateAnswers = async () => {
    setGeneratingAnswers(true);
    try {
      const params = {};
      if (selectedCategory) params.domain = selectedCategory;
      if (selectedSubcategory) params.subdomain = selectedSubcategory;
      const res = await interviewAPI.generateAnswers(workspaceId, params);
      if (res.data.updated_count > 0) {
        toast.success(`${res.data.message}`);
        loadData();
      } else {
        toast.info(res.data.message || "Toutes les questions ont déjà une réponse.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération des réponses");
    } finally { setGeneratingAnswers(false); }
  };

  const getCategoryCount = (domain) => {
    const cat = categoryTree[domain];
    if (!cat) return 0;
    return cat.questions.length + Object.values(cat.subdomains).reduce((sum, qs) => sum + qs.length, 0);
  };

  if (loading && questions.length === 0) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden animate-fade-in">
        {/* ===== SIDEBAR ===== */}
        <div className="w-64 min-w-[16rem] border-r bg-card flex flex-col overflow-hidden">
          {/* Sidebar Header */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/workspace/${workspaceId}/interviews`)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Catégories</h2>
            </div>
            <p className="text-xs text-muted-foreground">{questions.length} question(s) au total</p>
          </div>

          {/* Category Tree */}
          <div className="flex-1 overflow-y-auto py-2 scrollbar-thin">
            {/* All Questions */}
            <button
              onClick={() => { setSelectedCategory(null); setSelectedSubcategory(null); }}
              className={cn(
                "sidebar-item w-full text-left",
                !selectedCategory && "active"
              )}
            >
              <BookOpen className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">Toutes les questions</span>
              <span className="text-xs text-muted-foreground">{questions.length}</span>
            </button>

            <div className="my-2 mx-3 border-t" />

            {Object.entries(categoryTree).sort((a, b) => {
              const order = Object.keys(CATEGORY_CONFIG);
              return (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0]));
            }).map(([domain, data]) => {
              const config = CATEGORY_CONFIG[domain] || { emoji: "📂", color: "text-muted-foreground" };
              const count = getCategoryCount(domain);
              const isExpanded = expandedCategories[domain] !== false;
              const subdomains = Object.entries(data.subdomains).sort((a, b) => a[0].localeCompare(b[0]));
              const hasSubdomains = subdomains.length > 0;

              return (
                <div key={domain}>
                  <button
                    onClick={() => { selectCategory(domain); if (hasSubdomains) toggleCategory(domain); }}
                    className={cn(
                      "sidebar-item w-full text-left",
                      selectedCategory === domain && !selectedSubcategory && "active"
                    )}
                  >
                    {hasSubdomains ? (
                      isExpanded ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    ) : <span className="w-3.5" />}
                    <span className="flex-shrink-0">{config.emoji}</span>
                    <span className="flex-1 truncate font-medium text-sm">{domain.replace("Entretien ", "")}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
                  </button>

                  {/* Subdomains */}
                  {isExpanded && hasSubdomains && (
                    <div className="ml-6">
                      {subdomains.map(([sub, qs]) => (
                        <button
                          key={sub}
                          onClick={() => { setSelectedCategory(domain); selectSubcategory(sub); }}
                          className={cn(
                            "w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs rounded-lg transition-all duration-200",
                            selectedSubcategory === sub
                              ? "bg-primary/10 text-primary font-semibold"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted"
                          )}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                          <span className="flex-1 truncate">{sub}</span>
                          <span className="text-[10px] text-muted-foreground tabular-nums">{qs.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sidebar Footer - Seed */}
          {questions.length === 0 && (
            <div className="p-4 border-t">
              <Button onClick={handleSeed} disabled={seeding} className="w-full gap-2" size="sm">
                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                Charger 119 questions
              </Button>
            </div>
          )}
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-card/50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-xl font-bold tracking-tight">
                  {selectedSubcategory || (selectedCategory ? selectedCategory.replace("Entretien ", "") : "Banque de Questions")}
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">{filteredQuestions.filtered.length} question(s)</p>
              </div>
              <div className="flex gap-2">
                {questions.length === 0 && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSeed} disabled={seeding}>
                    {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
                    Charger les questions
                  </Button>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/workspace/${workspaceId}/interviews/import`)}>
                  <Upload className="h-3.5 w-3.5" /> Import
                </Button>
                {isAdmin && (
                  <>
                    <Button variant="outline" size="sm" className="gap-1.5 border-amber-300 text-amber-600 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10" onClick={handleGenerateAnswers} disabled={generatingAnswers}>
                      {generatingAnswers ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                      {generatingAnswers ? "Génération..." : "Réponses (IA)"}
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-500/40 dark:text-purple-400 dark:hover:bg-purple-500/10" onClick={() => { setAiForm({ domain: selectedCategory || "Entretien technique", subdomain: selectedSubcategory || "", difficulty: "mixed", count: 5, customDomain: "", customSubdomain: "" }); setAiDialogOpen(true); }}>
                      <Sparkles className="h-3.5 w-3.5" /> Générer (IA)
                    </Button>
                  </>
                )}
                <Button size="sm" className="gap-1.5" onClick={openCreate}>
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une question..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-8 text-sm"
                />
              </div>
              <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                <SelectTrigger className="w-[160px] h-8 text-sm">
                  <SelectValue placeholder="Tous les niveaux" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Toutes les difficultés</SelectItem>
                  {Object.entries(DIFFICULTY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Questions List */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-1 scrollbar-thin">
            {filteredQuestions.filtered.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <HelpCircle className="h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 font-semibold">Aucune question</h3>
                  <p className="mt-2 text-sm text-muted-foreground text-center max-w-md">
                    {questions.length === 0
                      ? "Commencez par charger les 119 questions DevOps ou créez vos propres questions."
                      : "Aucune question ne correspond aux filtres sélectionnés."}
                  </p>
                  {questions.length === 0 && (
                    <div className="mt-6 flex gap-3">
                      <Button onClick={handleSeed} disabled={seeding} className="gap-2">
                        {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                        Charger 119 questions DevOps
                      </Button>
                      <Button variant="outline" className="gap-2" onClick={openCreate}>
                        <Plus className="h-4 w-4" /> Créer manuellement
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              Object.entries(filteredQuestions.grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([groupName, groupQuestions]) => (
                <div key={groupName} className="mb-5">
                  {/* Subcategory Header */}
                  {Object.keys(filteredQuestions.grouped).length > 1 && (
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{groupName}</span>
                      <span className="text-[10px] text-muted-foreground">({groupQuestions.length})</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  )}

                  {groupQuestions.map((q, idx) => (
                    <div
                      key={q.id}
                      className="group rounded-xl border bg-card hover:shadow-md transition-all duration-200 mb-2"
                    >
                      {/* Question Row */}
                      <div className="px-4 py-3 flex items-start gap-3">
                        {/* Number */}
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold mt-0.5">
                          {String(idx + 1).padStart(2, "0")}
                        </span>

                        {/* Question Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-relaxed">{q.question_text}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 border", DIFFICULTY_COLORS[q.difficulty] || "")}>
                              {DIFFICULTY_LABELS[q.difficulty] || q.difficulty}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {TYPE_LABELS[q.type] || q.type}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">~{q.estimated_minutes}min</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-muted-foreground hover:text-primary"
                            onClick={(e) => { e.stopPropagation(); toggleAnswer(q.id); }}
                          >
                            {expandedAnswers[q.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            <span className="text-[10px] ml-1">Réponse</span>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); openEdit(q); }}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteId(q.id); }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Answer */}
                      {expandedAnswers[q.id] && q.expected_answer && (
                        <div className="mx-4 mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-sm">💡</span>
                            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Réponse type</span>
                          </div>
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{q.expected_answer}</p>
                          {q.key_points?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-500/20">
                              <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-500/70 uppercase mb-1">Points clés</p>
                              <div className="flex flex-wrap gap-1.5">
                                {q.key_points.map((kp, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-muted border text-foreground">
                                    {kp}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ===== CREATE/EDIT DIALOG ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editQuestion ? "Modifier la question" : "Nouvelle question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Question *</Label>
              <Textarea value={form.question_text} onChange={(e) => setForm({...form, question_text: e.target.value})}
                placeholder="Texte de la question..." rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Niveau</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({...form, difficulty: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(DIFFICULTY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Score max</Label>
                <Input type="number" value={form.max_score} onChange={(e) => setForm({...form, max_score: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Domaine</Label>
                <Select value={form.domain || "__none__"} onValueChange={(v) => setForm({...form, domain: v === "__none__" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Aucun —</SelectItem>
                    {Object.keys(CATEGORY_CONFIG).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sous-domaine</Label>
                <Input value={form.subdomain} onChange={(e) => setForm({...form, subdomain: e.target.value})} placeholder="Ex: Docker, Kubernetes..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Coefficient</Label>
                <Input type="number" step="0.1" value={form.weight} onChange={(e) => setForm({...form, weight: parseFloat(e.target.value) || 1})} />
              </div>
              <div className="space-y-2">
                <Label>Temps estimé (min)</Label>
                <Input type="number" value={form.estimated_minutes} onChange={(e) => setForm({...form, estimated_minutes: parseInt(e.target.value) || 5})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tags (séparés par des virgules)</Label>
              <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="kubernetes, containers" />
            </div>
            <div className="space-y-2">
              <Label>Réponse attendue</Label>
              <Textarea value={form.expected_answer} onChange={(e) => setForm({...form, expected_answer: e.target.value})}
                placeholder="Éléments de réponse attendus..." rows={4} />
            </div>
            <div className="space-y-2">
              <Label>Points clés (un par ligne)</Label>
              <Textarea value={form.key_points} onChange={(e) => setForm({...form, key_points: e.target.value})}
                placeholder={"Point clé 1\nPoint clé 2\nPoint clé 3"} rows={3} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editQuestion ? "Mettre à jour" : "Créer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== AI GENERATION DIALOG ===== */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-lg">Générer des questions (IA)</span>
                <p className="text-xs text-muted-foreground font-normal mt-0.5">L'IA génère les questions avec réponses types, points clés et difficulté</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-3">

            {/* Category (Domain) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Catégorie *</Label>
              <Select
                value={aiForm.domain === "__custom__" ? "__custom__" : aiForm.domain || "__none__"}
                onValueChange={(v) => {
                  if (v === "__custom__") {
                    setAiForm({...aiForm, domain: "__custom__", customDomain: "", subdomain: "", customSubdomain: ""});
                  } else {
                    setAiForm({...aiForm, domain: v, customDomain: "", subdomain: "", customSubdomain: ""});
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CATEGORY_CONFIG).map(d => (
                    <SelectItem key={d} value={d}>
                      {(CATEGORY_CONFIG[d]?.emoji || "📂") + " " + d}
                    </SelectItem>
                  ))}
                  {/* Show custom domains from questions that aren't in CATEGORY_CONFIG */}
                  {Object.keys(categoryTree).filter(d => !CATEGORY_CONFIG[d]).map(d => (
                    <SelectItem key={d} value={d}>📂 {d}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">
                    <span className="flex items-center gap-1.5">
                      <Plus className="h-3.5 w-3.5" /> Nouvelle catégorie...
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {aiForm.domain === "__custom__" && (
                <Input
                  value={aiForm.customDomain || ""}
                  onChange={(e) => setAiForm({...aiForm, customDomain: e.target.value})}
                  placeholder="Nom de la nouvelle catégorie..."
                  className="mt-2"
                  autoFocus
                />
              )}
            </div>

            {/* Subcategory (Subdomain) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sous-catégorie</Label>
              {(() => {
                const currentDomain = aiForm.domain === "__custom__" ? null : aiForm.domain;
                const existingSubs = currentDomain && categoryTree[currentDomain]
                  ? Object.keys(categoryTree[currentDomain].subdomains).sort()
                  : [];
                return (
                  <>
                    <Select
                      value={aiForm.subdomain === "__custom__" ? "__custom__" : aiForm.subdomain || "__none__"}
                      onValueChange={(v) => {
                        if (v === "__custom__") {
                          setAiForm({...aiForm, subdomain: "__custom__", customSubdomain: ""});
                        } else if (v === "__none__") {
                          setAiForm({...aiForm, subdomain: "", customSubdomain: ""});
                        } else {
                          setAiForm({...aiForm, subdomain: v, customSubdomain: ""});
                        }
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Optionnel — Choisir un sous-domaine" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Aucun (général) —</SelectItem>
                        {existingSubs.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">
                          <span className="flex items-center gap-1.5">
                            <Plus className="h-3.5 w-3.5" /> Nouveau sous-domaine...
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {aiForm.subdomain === "__custom__" && (
                      <Input
                        value={aiForm.customSubdomain || ""}
                        onChange={(e) => setAiForm({...aiForm, customSubdomain: e.target.value})}
                        placeholder="Ex: Prometheus, Terraform Cloud, EKS..."
                        className="mt-2"
                        autoFocus
                      />
                    )}
                  </>
                );
              })()}
            </div>

            {/* Difficulty */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Difficulté</Label>
              <div className="grid grid-cols-5 gap-1.5">
                {[
                  { value: "mixed", label: "🎲 Mixte", desc: "Variée" },
                  { value: "junior", label: "🟢 Junior", desc: "Débutant" },
                  { value: "confirmed", label: "🔵 Confirmé", desc: "3-5 ans" },
                  { value: "senior", label: "🟠 Senior", desc: "5-8 ans" },
                  { value: "expert", label: "🔴 Expert", desc: "8+ ans" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAiForm({...aiForm, difficulty: opt.value})}
                    className={cn(
                      "flex flex-col items-center gap-0.5 p-2 rounded-lg border-2 text-xs transition-all duration-200",
                      aiForm.difficulty === opt.value
                        ? "border-purple-500 bg-purple-50 text-purple-700 shadow-sm dark:bg-purple-500/15 dark:text-purple-400 dark:border-purple-500"
                        : "border-muted hover:border-purple-300 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <span className="text-sm">{opt.label.split(" ")[0]}</span>
                    <span className="font-medium">{opt.label.split(" ").slice(1).join(" ")}</span>
                  </button>
                ))}
              </div>
              {aiForm.difficulty === "mixed" && (
                <p className="text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 p-2 rounded-lg">
                  ✨ L'IA va varier les niveaux de difficulté automatiquement pour chaque question générée
                </p>
              )}
            </div>

            {/* Count */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Nombre de questions</Label>
                <span className="text-lg font-bold text-purple-600 dark:text-purple-400 tabular-nums">{aiForm.count}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">1</span>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={aiForm.count}
                  onChange={(e) => setAiForm({...aiForm, count: parseInt(e.target.value)})}
                  className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <span className="text-xs text-muted-foreground">20</span>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground px-4">
                <span>Rapide</span>
                <span>Détaillé</span>
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Résumé</p>
              <div className="text-sm space-y-1">
                <p>📂 <strong>{aiForm.domain === "__custom__" ? (aiForm.customDomain || "—") : (aiForm.domain || "—")}</strong></p>
                {(aiForm.subdomain && aiForm.subdomain !== "__none__") && (
                  <p>📌 {aiForm.subdomain === "__custom__" ? (aiForm.customSubdomain || "—") : aiForm.subdomain}</p>
                )}
                <p>🎯 {aiForm.count} question(s) · Difficulté : <strong>{
                  aiForm.difficulty === "mixed" ? "Mixte" :
                  DIFFICULTY_LABELS[aiForm.difficulty] || aiForm.difficulty
                }</strong></p>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Chaque question inclura : réponse type + points clés + niveau</p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAiDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={() => {
                  // Resolve custom values before submitting
                  const finalDomain = aiForm.domain === "__custom__" ? (aiForm.customDomain || "").trim() : aiForm.domain;
                  const finalSubdomain = aiForm.subdomain === "__custom__" ? (aiForm.customSubdomain || "").trim() : (aiForm.subdomain === "__none__" ? "" : aiForm.subdomain || "");
                  if (!finalDomain) { toast.error("Sélectionnez ou créez une catégorie"); return; }
                  setAiForm(prev => ({...prev, _resolvedDomain: finalDomain, _resolvedSubdomain: finalSubdomain}));
                  handleAIGenerate(finalDomain, finalSubdomain);
                }}
                disabled={aiGenerating}
                className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              >
                {aiGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiGenerating ? "Génération en cours..." : `Générer ${aiForm.count} question(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DELETE CONFIRMATION ===== */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette question ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkspaceLayout>
  );
}
